import { useEffect, useMemo, useState } from 'react';
import type { FocusTask } from '../../lib/focus';
import { listAllTasks, type TaskSummary } from '../../lib/tasks';
import { UNFINISHED_STATUSES } from '../../lib/taskStatus';
import { formatDuration } from '../../lib/duration';
import { TaskCombobox } from '../tasks/TaskCombobox';
import { Alert, Button, Card, SkeletonList } from '../ui';
import { TargetIcon } from '../icons';
import { FOCUS_GRID } from './focusGrid';
import { FocusTaskRow } from './FocusTaskRow';
import type { FocusSessionState } from './useFocusSession';

/**
 * What you picked to work on now.
 *
 * The rows only. Everything that fetches or changes them lives in
 * useFocusSession on the page, because the task panel opens over the dashboard
 * and has to write its edits back into this list -- the same arrangement, and
 * the same reason, as DayTasksCard.
 *
 * The card is always on the page, but it is one line until a session exists.
 * Two things fall out of that. A day with nothing due can still start a session,
 * which a card that only appeared once you had picked something could not do.
 * And nobody has to learn the feature from a changelog: the line is there,
 * saying what it is for, on the screen you already open every morning.
 */
export function FocusCard({
  className = '',
  focus,
  activeTaskId,
  onEnded,
}: {
  className?: string;
  focus: FocusSessionState;
  /** The task open in the panel, so its row is marked underneath. */
  activeTaskId: string | null;
  /** Announced by the page, which owns the toast. */
  onEnded: (message: string) => void;
}) {
  const {
    session,
    loading,
    loadError,
    actionError,
    setActionError,
    runningTaskId,
    busyTaskId,
    ending,
    clearing,
    add,
    remove,
    clearDone,
    patch,
    toggleTimer,
    end,
  } = focus;

  const tasks = useMemo(() => inWorkingOrder(session?.tasks ?? []), [session]);
  const done = tasks.filter((task) => task.status === 'DONE').length;

  return (
    <Card className={className}>
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-stone-200 px-4 py-3">
        <div className="min-w-0">
          <h2 className="flex items-center gap-2 text-lg font-semibold tracking-tight">
            <TargetIcon className="h-4 w-4 text-honey-600" filled={Boolean(session)} />
            Focus
          </h2>
          <p className="mt-0.5 text-meta text-stone-500">
            {session
              ? `Started ${startedAtLabel(session.startedAt)} · ${done} of ${session.tasks.length} done`
              : 'Pick what you are working on this session.'}
          </p>
        </div>

        {session ? (
          <div className="flex items-center gap-2">
            {/*
              Only once there is something to clear. A control that spends the
              first half of every session greyed out is a control you learn to
              stop reading -- and the count beside it already says whether the
              button has anything to do.

              `ghost` against the secondary End session: they sit side by side,
              and the one that closes the sitting should be the one your eye
              lands on. Tidying up is the smaller act.
            */}
            {done > 0 ? (
              <Button
                variant="ghost"
                compact
                loading={clearing}
                onClick={() => void clearDone()}
                title="Take the finished ones off the list"
                aria-label={`Clear ${done} finished ${done === 1 ? 'task' : 'tasks'} from this session`}
              >
                Clear done
              </Button>
            ) : null}

            <Button
              compact
              loading={ending}
              onClick={() => {
                void end().then((summary) => {
                  if (summary) onEnded(summaryLabel(summary));
                });
              }}
            >
              End session
            </Button>
          </div>
        ) : null}
      </div>

      {actionError ? (
        <div className="border-b border-stone-100 p-3">
          <Alert onDismiss={() => setActionError(null)}>{actionError}</Alert>
        </div>
      ) : null}

      {loadError ? (
        <div className="p-4">
          <Alert>{loadError}</Alert>
        </div>
      ) : loading ? (
        <SkeletonList rows={2} bordered={false} />
      ) : (
        <>
          {session && tasks.length > 0 ? (
            <>
              {/* Same template as every row, so the labels sit over their
                  columns. Each `sr-only` label is wrapped rather than carrying
                  the class itself: `sr-only` is `position: absolute`, which
                  would take the element out of the grid flow and collapse the
                  column it names. */}
              <div
                className={`${FOCUS_GRID} border-b border-stone-100 px-4 py-1.5 text-micro font-medium uppercase tracking-wide text-stone-500`}
              >
                <span>
                  <span className="sr-only">Done</span>
                </span>
                <span>Task</span>
                <span className="hidden sm:block">Project</span>
                <span>
                  <span className="sr-only">Time</span>
                </span>
                {/* The row-action track. No label: the buttons under it are
                    hover actions that name themselves, and a header word for
                    them would be the widest thing in a 2.75rem column. */}
                <span aria-hidden="true" />
              </div>

              <ul className="divide-y divide-stone-100">
                {tasks.map((task) => (
                  <FocusTaskRow
                    key={task.id}
                    task={task}
                    active={task.id === activeTaskId}
                    running={runningTaskId === task.id}
                    busy={busyTaskId === task.id}
                    onPatch={(changes) => void patch(task, changes)}
                    onToggleTimer={() => void toggleTimer(task.id)}
                    onRemove={() => void remove(task.id)}
                  />
                ))}
              </ul>
            </>
          ) : null}

          <FocusAddBox picked={focus.taskIds} onPick={(task) => void add(task)} />
        </>
      )}
    </Card>
  );
}

/**
 * The rows, with what is still open on top.
 *
 * A session is a list you work down, so the next thing to do should be the next
 * thing you read -- and by the third tick the picks you have finished are
 * sitting between the ones you have not. Ticking a row sinks it; unticking it
 * brings it back up.
 *
 * Two filters rather than a `sort` comparator, which is a stable partition for
 * free: inside each half the picks stay in the order the session made them,
 * oldest first, the way the server hands them back. A comparator over a boolean
 * would say nothing about the rest and leave the order to the engine.
 *
 * Ordered here rather than in the hook because it is a way of reading the
 * session, not a fact about it: `completedAt`, the count in the header, and
 * whatever the panel writes back all go on meaning the same thing.
 */
function inWorkingOrder(tasks: FocusTask[]): FocusTask[] {
  const open = tasks.filter((task) => task.status !== 'DONE');
  // Nothing to move -- hand back the same array rather than a copy of it.
  if (open.length === 0 || open.length === tasks.length) return tasks;
  return [...open, ...tasks.filter((task) => task.status === 'DONE')];
}

/**
 * Add a task by name.
 *
 * The one-click way in is the target button on a day row; this is for everything
 * else -- the thing that is not due today that you nonetheless mean to finish
 * this hour, which turns out to be most of what a session is made of.
 *
 * Options are fetched once, lazily, when the box is first focused rather than
 * when the card mounts. Most dashboard loads never touch it, and the card is on
 * the screen this app opens on.
 *
 * Nothing here goes inert while a pick is in flight, because a pick is never in
 * flight as far as this box is concerned: the row appears at once, and the task
 * drops out of the options below on the same paint. Picking two in a row is a
 * thing people do, and it should cost two keystrokes rather than two waits.
 */
function FocusAddBox({
  picked,
  onPick,
}: {
  picked: Set<string>;
  onPick: (task: TaskSummary) => void;
}) {
  const [wanted, setWanted] = useState(false);
  const [options, setOptions] = useState<TaskSummary[]>([]);

  useEffect(() => {
    if (!wanted) return;
    const controller = new AbortController();

    // Capped, and sorted by due date so the cap keeps the most pressing end of
    // the list. A workspace big enough to overflow 200 unfinished tasks is one
    // where the search box wants to be a server query -- but it is not one yet,
    // and TaskCombobox filters what it is given for free.
    listAllTasks(
      { status: UNFINISHED_STATUSES, sort: 'due', limit: 200 },
      controller.signal,
    )
      .then((data) => setOptions(data.tasks))
      // Silent: the box still works for whatever it already has, and a red
      // sentence under a search field nobody has typed in yet is noise.
      .catch(() => {});

    return () => controller.abort();
  }, [wanted]);

  return (
    <div className="px-4 py-3" onFocusCapture={() => setWanted(true)}>
      <TaskCombobox
        compact
        label="Add a task to this session"
        placeholder="Add a task…"
        // Already-picked tasks are dropped rather than shown and refused: the
        // list is what you can add, and an option that answers "already there"
        // is a dead end you had to type to find.
        options={options.filter((task) => !picked.has(task.id))}
        onSelect={onPick}
      />
    </div>
  );
}

/** `10:14` in the reader's own clock -- a session is measured in the hours of
 *  their day, not in dates. */
function startedAtLabel(startedAt: string): string {
  return new Date(startedAt).toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * What the session came to, as a sentence.
 *
 * Says what happened to the unfinished ones rather than leaving them as a
 * subtraction: "3 of 4" invites the reader to notice a failure, "1 back on the
 * pile" says the true thing, which is that they are still there and still fine.
 */
function summaryLabel(summary: {
  planned: number;
  completed: number;
  trackedSeconds: number;
}): string {
  const { planned, completed, trackedSeconds } = summary;
  const returned = planned - completed;

  const parts = [`${completed} of ${planned} done`];
  if (returned > 0) parts.push(`${returned} back on the pile`);
  if (trackedSeconds > 0) parts.push(`${formatDuration(trackedSeconds)} logged`);

  return `Session ended — ${parts.join(', ')}.`;
}
